import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { DEFAULT_CONTENT } from "../lib/defaultContent";

const ContentContext = createContext(null);

function deepMerge(base, override) {
  if (override === null || override === undefined) return base;
  if (Array.isArray(override)) return override;
  if (typeof override !== "object") return override;
  const result = { ...base };
  for (const k of Object.keys(override)) {
    if (k.startsWith("_")) continue;
    if (override[k] && typeof override[k] === "object" && !Array.isArray(override[k]) && base?.[k] && typeof base[k] === "object" && !Array.isArray(base[k])) {
      result[k] = deepMerge(base[k], override[k]);
    } else {
      result[k] = override[k];
    }
  }
  return result;
}

export function ContentProvider({ children }) {
  const [serverContent, setServerContent] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setServerContent((prev) => deepMerge(prev || {}, data || {}));
    } catch {
      setServerContent((prev) => prev || {});
    } finally {
      setLoaded(true);
    }
  }, []);

  const refreshSection = useCallback(async (sectionKey) => {
    const { data } = await api.get(`/content/section/${sectionKey}`);
    setServerContent((prev) => deepMerge(prev || {}, { [data.key]: data.value, _updated_at: data._updated_at }));
    return data;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const content = deepMerge(DEFAULT_CONTENT, serverContent || {});

  const save = async (next) => {
    const { data } = await api.put("/admin/content", { data: next });
    setServerContent((prev) => deepMerge(prev || {}, data || {}));
    return data;
  };

  const saveSection = async (sectionKey, value) => {
    const { data } = await api.put(`/admin/content/section/${sectionKey}`, { value });
    setServerContent((prev) => deepMerge(prev || {}, { [data.key]: data.value, _updated_at: data._updated_at }));
    return data;
  };

  return (
    <ContentContext.Provider value={{ content, loaded, refresh, refreshSection, save, saveSection }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useSiteContent = () => useContext(ContentContext);

// Resolve image URLs that may be relative paths (/api/uploads/xxx)
export const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};
